import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalContent, ModalHeader } from '@forge/common';
import '../activity.scss';
import { useTranslation } from 'react-i18next';
import { IActivityItem, IActivityTask } from '../../Redux/activityModels';
import { formatDate, translateWorkflowTaskDescription } from '../../utils';
import { ActivityAppDispatch, useActivitySelector } from '../../Redux/activityStore';
import { retryTask } from '../../Redux/activitySlice';
import { selectRetryingTaskIds } from '../../Redux/activitySelectors';
import ActivityStatusBadge from '../shared/ActivityStatusBadge';
import ActivityTaskItem from './ActivityTaskItem';

export interface ActivityDetailModalProps {
  item: IActivityItem;
  onClose: () => void;
}

const ActivityDetailModal: React.FC<ActivityDetailModalProps> = ({ item, onClose }) => {
  const { description, siteName, status, createdAt, updatedAt, tasks, correlationId } = item;
  const dispatch = useDispatch<ActivityAppDispatch>();
  const retryingTaskIds = useActivitySelector(selectRetryingTaskIds);
  const { t } = useTranslation();

  const handleRetry = useCallback((taskId: string) => {
    dispatch(retryTask(taskId));
  }, [dispatch]);

  return (
    <Modal onModalClose={onClose} onBackdropClick={onClose} className="activity-detail-modal">
      <ModalHeader
        onModalClose={onClose}
        className="!tw-mb-0"
      >
        <p className="tw-text-sm tw-m-0">{translateWorkflowTaskDescription(description, t)}</p>
      </ModalHeader>
      <ModalContent className="tw-flex tw-flex-col tw-overflow-y-auto">

        {/* Padded upper section */}
        <div className="tw-px-6 tw-pb-4">

          {/* Site name */}
          <h2 className="tw-text-xl activity-text-primary tw-m-0 tw-mb-3">{siteName}</h2>

          {/* Status badge */}
          <div className="tw-mb-3">
            <ActivityStatusBadge status={status} showFailedIcon={false} />
          </div>

          {/* Dates */}
          <p className="tw-text-xs activity-text-secondary tw-m-0">
            {`${t('activityLogModalCreatedOn')} ${formatDate(createdAt)}`}
            {updatedAt && (
              <>{' | '}{`${t('activityLogModalLastUpdatedOn')} ${formatDate(updatedAt)}`}</>
            )}
          </p>

        </div>

        {/* Tasks + footer — fills remaining modal height with background */}
        <div className="tw-flex tw-flex-col tw-bg-white/5">

          {tasks.length > 0 && (
            <div className="tw-flex tw-flex-col" data-testid="task-list">
              {tasks.map((task: IActivityTask, index: number) => (
                <ActivityTaskItem
                  key={task.id}
                  task={task}
                  index={index}
                  isRetrying={retryingTaskIds.includes(task.id)}
                  onRetry={handleRetry}
                  correlationId={correlationId}
                />
              ))}
            </div>
          )}

          {/* Support footer */}
          <div className={`tw-flex tw-flex-col tw-gap-1 tw-px-6 tw-pt-4 tw-pb-6${tasks.length > 0 ? ' tw-border-t activity-border-divider' : ''}`}>
            <p className="tw-text-sm activity-text-secondary tw-m-0">
              {t('activityLogModalSupportReach')}{' '}
              <a
                href={`mailto:${t('activityLogModalSupportEmail')}`}
                className="activity-link-text hover:tw-underline"
              >
                {t('activityLogModalSupportEmail')}
              </a>
            </p>
          </div>

        </div>

      </ModalContent>
    </Modal>
  );
};

export default ActivityDetailModal;
